import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { isNil, pick, isEmpty } from "lodash";

import { Modal } from "@covertree/ui/modal/Modal";
import { ConditionData, UnitLevelUnderwritingAnswers } from "@covertree/types";
import { UnderwritingQuestionsForm } from "../validatorsAndTypes/underwritingQuestions";
import {
  getRelatedFormFields,
  resetRelatedFields,
} from "../utils/underwritingQuestions";

type Props = {
  condition: ConditionData | null;
  hasAnswerChanged: boolean;
};

export function UnderwritingQuestionsModal({
  condition,
  hasAnswerChanged,
}: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [valuesOnModalOpen, setValuesOnModalOpen] =
    useState<UnderwritingQuestionsForm>({});

  const { getValues, setValue } = useFormContext<UnderwritingQuestionsForm>();

  const relatedConditionQuestionsKeys = useMemo(
    () =>
      condition?.questionKey ? getRelatedFormFields(condition.questionKey) : [],
    [condition]
  );

  useEffect(() => {
    setIsModalOpen(!!condition);
    const formStateValues = getValues();
    setValuesOnModalOpen({
      ...pick(formStateValues, relatedConditionQuestionsKeys),
    });
  }, [condition, relatedConditionQuestionsKeys]);

  const resetFieldAndRelatedFields = useCallback(() => {
    if (condition?.questionKey) {
      setValue(condition.questionKey, "No", {
        shouldValidate: true,
      });
      resetRelatedFields(setValue, condition.questionKey);
    }
  }, [condition]);

  const handleCloseModal = useCallback(() => {
    if (
      condition?.questionKey === UnitLevelUnderwritingAnswers.AnimalBite &&
      !hasAnswerChanged
    ) {
      setIsModalOpen(false);
      return;
    }

    if (
      (!isEmpty(valuesOnModalOpen) &&
        relatedConditionQuestionsKeys.length > 0) ||
      condition?.questionKey === UnitLevelUnderwritingAnswers.AnimalBite
    ) {
      if (
        Object.values(valuesOnModalOpen).every((value) => isNil(value)) ||
        (condition?.questionKey ===
          UnitLevelUnderwritingAnswers.BusinessOnPremises &&
          Object.values(valuesOnModalOpen).every(
            (value) => isNil(value) || value === "Zero"
          ))
      ) {
        // reset state for related fields and set question to No
        resetFieldAndRelatedFields();
      } else {
        // restore state to values before modal open
        relatedConditionQuestionsKeys.forEach((key) => {
          setValue(key, valuesOnModalOpen[key], {
            shouldValidate: true,
          });
        });
      }
    }
    setIsModalOpen(false);
  }, [valuesOnModalOpen, relatedConditionQuestionsKeys, condition]);

  if (!condition) {
    return <></>;
  }

  return (
    <Modal
      title={condition.modalTitle ?? condition.title}
      open={isModalOpen}
      onClose={handleCloseModal}
      maxWidth="sm"
      sx={{
        paddingLeft: "0px !important",
        paddingRight: "0px !important",
      }}>
      {condition.getModalContent?.(() => {
        setIsModalOpen(false);
      })}
    </Modal>
  );
}
